import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from 'react';
import {
  TRoute,
  TVehicle,
  TRouteAssignment,
  TDriver,
  TViewType,
  IRoutesResponse,
  IVehiclesResponse,
  IRouteAssignmentsResponse,
  IDriversResponse,
} from 'src/apis/administration/administration.interfaces';
import { Box } from '@mui/material';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { useRouteListHook } from '../hooks/useRouteListHook';
import { useVehicleListHook } from '../hooks/useVehicleListHook';
import { useDriverListHook } from '../hooks/useDriverListHook';
import { useAssignmentListHook } from '../hooks/useAssignmentListHook';

type TEditMode = 'create' | 'edit' | 'view';

export enum EViewState {
  LIST = 'list',
  DETAILS = 'details',
  EDIT = 'edit',
}

type TRoutesAndScheduleState = {
  routes: TRoute[];
  vehicles: TVehicle[];
  routeAssignments: TRouteAssignment[];
  drivers: TDriver[];
  selectedRoute?: TRoute;
  selectedVehicle?: TVehicle;
  selectedAssignment?: TRouteAssignment;
  selectedDriver?: TDriver;
  isLoading: boolean;
  errorMessage?: string;
  editMode?: TEditMode;
  viewType?: TViewType;
  viewState: EViewState;
  paginationParams: TPaginationParams;
  routesCount: number;
  vehiclesCount: number;
  assignmentsCount: number;
  driversCount: number;
};

export enum ACTION_TYPE {
  SET_ROUTES = 'SET_ROUTES',
  SET_VEHICLES = 'SET_VEHICLES',
  SET_ROUTE_ASSIGNMENTS = 'SET_ROUTE_ASSIGNMENTS',
  SET_DRIVERS = 'SET_DRIVERS',
  SELECT_ROUTE = 'SELECT_ROUTE',
  SELECT_VEHICLE = 'SELECT_VEHICLE',
  SELECT_ASSIGNMENT = 'SELECT_ASSIGNMENT',
  SELECT_DRIVER = 'SELECT_DRIVER',
  ADD_ROUTE = 'ADD_ROUTE',
  UPDATE_ROUTE = 'UPDATE_ROUTE',
  DELETE_ROUTE = 'DELETE_ROUTE',
  ADD_VEHICLE = 'ADD_VEHICLE',
  UPDATE_VEHICLE = 'UPDATE_VEHICLE',
  DELETE_VEHICLE = 'DELETE_VEHICLE',
  ADD_ASSIGNMENT = 'ADD_ASSIGNMENT',
  UPDATE_ASSIGNMENT = 'UPDATE_ASSIGNMENT',
  DELETE_ASSIGNMENT = 'DELETE_ASSIGNMENT',
  ADD_DRIVER = 'ADD_DRIVER',
  UPDATE_DRIVER = 'UPDATE_DRIVER',
  DELETE_DRIVER = 'DELETE_DRIVER',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_EDIT_MODE = 'SET_EDIT_MODE',
  SET_VIEW_TYPE = 'SET_VIEW_TYPE',
  SET_VIEW_STATE = 'SET_VIEW_STATE',
  SET_PAGINATION_PARAMS = 'SET_PAGINATION_PARAMS',
}

type Action =
  | { type: ACTION_TYPE.SET_ROUTES; payload: IRoutesResponse }
  | { type: ACTION_TYPE.SET_VEHICLES; payload: IVehiclesResponse }
  | {
      type: ACTION_TYPE.SET_ROUTE_ASSIGNMENTS;
      payload: IRouteAssignmentsResponse;
    }
  | { type: ACTION_TYPE.SET_DRIVERS; payload: IDriversResponse }
  | { type: ACTION_TYPE.SELECT_ROUTE; payload: TRoute | undefined }
  | { type: ACTION_TYPE.SELECT_VEHICLE; payload: TVehicle | undefined }
  | {
      type: ACTION_TYPE.SELECT_ASSIGNMENT;
      payload: TRouteAssignment | undefined;
    }
  | { type: ACTION_TYPE.SELECT_DRIVER; payload: TDriver | undefined }
  | { type: ACTION_TYPE.ADD_ROUTE; payload: TRoute }
  | { type: ACTION_TYPE.UPDATE_ROUTE; payload: TRoute }
  | { type: ACTION_TYPE.DELETE_ROUTE; payload: string }
  | { type: ACTION_TYPE.ADD_VEHICLE; payload: TVehicle }
  | { type: ACTION_TYPE.UPDATE_VEHICLE; payload: TVehicle }
  | { type: ACTION_TYPE.DELETE_VEHICLE; payload: string }
  | { type: ACTION_TYPE.ADD_ASSIGNMENT; payload: TRouteAssignment }
  | { type: ACTION_TYPE.UPDATE_ASSIGNMENT; payload: TRouteAssignment }
  | { type: ACTION_TYPE.DELETE_ASSIGNMENT; payload: string }
  | { type: ACTION_TYPE.ADD_DRIVER; payload: TDriver }
  | { type: ACTION_TYPE.UPDATE_DRIVER; payload: TDriver }
  | { type: ACTION_TYPE.DELETE_DRIVER; payload: string }
  | { type: ACTION_TYPE.SET_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_ERROR; payload: string | undefined }
  | { type: ACTION_TYPE.SET_EDIT_MODE; payload: TEditMode | undefined }
  | { type: ACTION_TYPE.SET_VIEW_TYPE; payload: TViewType | undefined }
  | { type: ACTION_TYPE.SET_VIEW_STATE; payload: EViewState }
  | { type: ACTION_TYPE.SET_PAGINATION_PARAMS; payload: TPaginationParams };

const initialState: TRoutesAndScheduleState = {
  routes: [],
  vehicles: [],
  routeAssignments: [],
  drivers: [],
  selectedRoute: undefined,
  selectedVehicle: undefined,
  selectedAssignment: undefined,
  selectedDriver: undefined,
  isLoading: false,
  errorMessage: undefined,
  editMode: undefined,
  viewType: undefined,
  viewState: EViewState.LIST,
  paginationParams: {
    page: 1,
    limit: 10,
  },
  routesCount: 0,
  vehiclesCount: 0,
  assignmentsCount: 0,
  driversCount: 0,
};

const RoutesAndScheduleContext = createContext<
  TRoutesAndScheduleState | undefined
>(undefined);
const RoutesAndScheduleDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const routesAndScheduleReducer = (
  state: TRoutesAndScheduleState,
  action: Action
): TRoutesAndScheduleState => {
  switch (action.type) {
    case ACTION_TYPE.SET_ROUTES:
      return {
        ...state,
        routes: action.payload.data,
        routesCount: action.payload.total,
      };
    case ACTION_TYPE.SET_VEHICLES:
      return {
        ...state,
        vehicles: action.payload.data,
        vehiclesCount: action.payload.total,
      };
    case ACTION_TYPE.SET_ROUTE_ASSIGNMENTS:
      return {
        ...state,
        routeAssignments: action.payload.data,
        assignmentsCount: action.payload.total,
      };
    case ACTION_TYPE.SET_DRIVERS:
      return {
        ...state,
        drivers: action.payload.data,
        driversCount: action.payload.total,
      };
    case ACTION_TYPE.SELECT_ROUTE:
      return { ...state, selectedRoute: action.payload };
    case ACTION_TYPE.SELECT_VEHICLE:
      return { ...state, selectedVehicle: action.payload };
    case ACTION_TYPE.SELECT_ASSIGNMENT:
      return { ...state, selectedAssignment: action.payload };
    case ACTION_TYPE.SELECT_DRIVER:
      return { ...state, selectedDriver: action.payload };
    case ACTION_TYPE.ADD_ROUTE:
      return { ...state, routes: [...state.routes, action.payload] };
    case ACTION_TYPE.UPDATE_ROUTE:
      return {
        ...state,
        routes: state.routes.map((route) =>
          route.id === action.payload.id ? action.payload : route
        ),
      };
    case ACTION_TYPE.DELETE_ROUTE:
      return {
        ...state,
        routes: state.routes.filter((route) => route.id !== action.payload),
      };
    case ACTION_TYPE.ADD_VEHICLE:
      return { ...state, vehicles: [...state.vehicles, action.payload] };
    case ACTION_TYPE.UPDATE_VEHICLE:
      return {
        ...state,
        vehicles: state.vehicles.map((vehicle) =>
          vehicle.id === action.payload.id ? action.payload : vehicle
        ),
      };
    case ACTION_TYPE.DELETE_VEHICLE:
      return {
        ...state,
        vehicles: state.vehicles.filter(
          (vehicle) => vehicle.id !== action.payload
        ),
      };
    case ACTION_TYPE.ADD_ASSIGNMENT:
      return {
        ...state,
        routeAssignments: [...state.routeAssignments, action.payload],
      };
    case ACTION_TYPE.UPDATE_ASSIGNMENT:
      return {
        ...state,
        routeAssignments: state.routeAssignments.map((assignment) =>
          assignment.id === action.payload.id ? action.payload : assignment
        ),
      };
    case ACTION_TYPE.DELETE_ASSIGNMENT:
      return {
        ...state,
        routeAssignments: state.routeAssignments.filter(
          (assignment) => assignment.id !== action.payload
        ),
      };
    case ACTION_TYPE.ADD_DRIVER:
      return { ...state, drivers: [...state.drivers, action.payload] };
    case ACTION_TYPE.UPDATE_DRIVER:
      return {
        ...state,
        drivers: state.drivers.map((driver) =>
          driver.id === action.payload.id ? action.payload : driver
        ),
      };
    case ACTION_TYPE.DELETE_DRIVER:
      return {
        ...state,
        drivers: state.drivers.filter((driver) => driver.id !== action.payload),
      };
    case ACTION_TYPE.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ACTION_TYPE.SET_ERROR:
      return { ...state, errorMessage: action.payload };
    case ACTION_TYPE.SET_EDIT_MODE:
      return { ...state, editMode: action.payload };
    case ACTION_TYPE.SET_VIEW_TYPE:
      return { ...state, viewType: action.payload };
    case ACTION_TYPE.SET_VIEW_STATE:
      return { ...state, viewState: action.payload };
    case ACTION_TYPE.SET_PAGINATION_PARAMS:
      return { ...state, paginationParams: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const RoutesAndScheduleProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(routesAndScheduleReducer, initialState);

  const { data: routesResponse, isLoading: isLoadingRoutes } = useRouteListHook(
    state.paginationParams
  );
  const { data: vehiclesResponse, isLoading: isLoadingVehicles } =
    useVehicleListHook(state.paginationParams);
  const { data: driversResponse, isLoading: isLoadingDrivers } =
    useDriverListHook(state.paginationParams);
  const { data: assignmentsResponse, isLoading: isLoadingAssignments } =
    useAssignmentListHook(state.paginationParams);

  const isLoading =
    isLoadingRoutes ||
    isLoadingVehicles ||
    isLoadingDrivers ||
    isLoadingAssignments;

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.SET_LOADING, payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    if (routesResponse) {
      dispatch({
        type: ACTION_TYPE.SET_ROUTES,
        payload: routesResponse,
      });
    }
  }, [routesResponse]);

  useEffect(() => {
    if (vehiclesResponse) {
      dispatch({
        type: ACTION_TYPE.SET_VEHICLES,
        payload: vehiclesResponse,
      });
    }
  }, [vehiclesResponse]);

  useEffect(() => {
    if (driversResponse) {
      dispatch({
        type: ACTION_TYPE.SET_DRIVERS,
        payload: driversResponse,
      });
    }
  }, [driversResponse]);

  useEffect(() => {
    if (assignmentsResponse) {
      dispatch({
        type: ACTION_TYPE.SET_ROUTE_ASSIGNMENTS,
        payload: assignmentsResponse,
      });
    }
  }, [assignmentsResponse]);

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.SELECT_ROUTE, payload: undefined });
    dispatch({ type: ACTION_TYPE.SELECT_VEHICLE, payload: undefined });
    dispatch({ type: ACTION_TYPE.SELECT_ASSIGNMENT, payload: undefined });
    dispatch({ type: ACTION_TYPE.SELECT_DRIVER, payload: undefined });
    dispatch({ type: ACTION_TYPE.SET_VIEW_STATE, payload: EViewState.LIST });
  }, [state.paginationParams]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading,
    }),
    [state, isLoading]
  );

  return (
    <Box
      data-testid="routes-schedule-provider"
      sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
    >
      <RoutesAndScheduleContext.Provider value={value}>
        <RoutesAndScheduleDispatchContext.Provider value={dispatch}>
          {children}
        </RoutesAndScheduleDispatchContext.Provider>
      </RoutesAndScheduleContext.Provider>
    </Box>
  );
};

export const useRoutesAndScheduleState = () => {
  const context = useContext(RoutesAndScheduleContext);
  if (context === undefined) {
    throw new Error(
      'useRoutesAndScheduleState must be used within a RoutesAndScheduleProvider'
    );
  }
  return context;
};

export const useRoutesAndScheduleActions = () => {
  const dispatch = useContext(RoutesAndScheduleDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useRoutesAndScheduleActions must be used within a RoutesAndScheduleProvider'
    );
  }

  const setRoutes = (routesResponse: IRoutesResponse) => {
    dispatch({ type: ACTION_TYPE.SET_ROUTES, payload: routesResponse });
  };

  const setVehicles = (vehiclesResponse: IVehiclesResponse) => {
    dispatch({ type: ACTION_TYPE.SET_VEHICLES, payload: vehiclesResponse });
  };

  const setRouteAssignments = (
    assignmentsResponse: IRouteAssignmentsResponse
  ) => {
    dispatch({
      type: ACTION_TYPE.SET_ROUTE_ASSIGNMENTS,
      payload: assignmentsResponse,
    });
  };

  const setDrivers = (driversResponse: IDriversResponse) => {
    dispatch({ type: ACTION_TYPE.SET_DRIVERS, payload: driversResponse });
  };

  const selectRoute = (route: TRoute | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_ROUTE, payload: route });
  };

  const selectVehicle = (vehicle: TVehicle | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_VEHICLE, payload: vehicle });
  };

  const selectAssignment = (assignment: TRouteAssignment | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_ASSIGNMENT, payload: assignment });
  };

  const selectDriver = (driver: TDriver | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_DRIVER, payload: driver });
  };

  const addRoute = (route: TRoute) => {
    dispatch({ type: ACTION_TYPE.ADD_ROUTE, payload: route });
  };

  const updateRoute = (route: TRoute) => {
    dispatch({ type: ACTION_TYPE.UPDATE_ROUTE, payload: route });
  };

  const deleteRoute = (id: string) => {
    dispatch({ type: ACTION_TYPE.DELETE_ROUTE, payload: id });
  };

  const addVehicle = (vehicle: TVehicle) => {
    dispatch({ type: ACTION_TYPE.ADD_VEHICLE, payload: vehicle });
  };

  const updateVehicle = (vehicle: TVehicle) => {
    dispatch({ type: ACTION_TYPE.UPDATE_VEHICLE, payload: vehicle });
  };

  const deleteVehicle = (id: string) => {
    dispatch({ type: ACTION_TYPE.DELETE_VEHICLE, payload: id });
  };

  const addAssignment = (assignment: TRouteAssignment) => {
    dispatch({ type: ACTION_TYPE.ADD_ASSIGNMENT, payload: assignment });
  };

  const updateAssignment = (assignment: TRouteAssignment) => {
    dispatch({ type: ACTION_TYPE.UPDATE_ASSIGNMENT, payload: assignment });
  };

  const deleteAssignment = (id: string) => {
    dispatch({ type: ACTION_TYPE.DELETE_ASSIGNMENT, payload: id });
  };

  const addDriver = (driver: TDriver) => {
    dispatch({ type: ACTION_TYPE.ADD_DRIVER, payload: driver });
  };

  const updateDriver = (driver: TDriver) => {
    dispatch({ type: ACTION_TYPE.UPDATE_DRIVER, payload: driver });
  };

  const deleteDriver = (id: string) => {
    dispatch({ type: ACTION_TYPE.DELETE_DRIVER, payload: id });
  };

  const setLoading = (isLoading: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_LOADING, payload: isLoading });
  };

  const setError = (errorMessage: string | undefined) => {
    dispatch({ type: ACTION_TYPE.SET_ERROR, payload: errorMessage });
  };

  const setEditMode = (editMode: TEditMode | undefined) => {
    dispatch({ type: ACTION_TYPE.SET_EDIT_MODE, payload: editMode });
  };

  const setViewType = (viewType: TViewType | undefined) => {
    dispatch({ type: ACTION_TYPE.SET_VIEW_TYPE, payload: viewType });
  };

  const setViewState = (viewState: EViewState) => {
    dispatch({ type: ACTION_TYPE.SET_VIEW_STATE, payload: viewState });
  };

  const updatePaginationParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_PAGINATION_PARAMS, payload: params });
  };

  return {
    setRoutes,
    setVehicles,
    setRouteAssignments,
    setDrivers,
    selectRoute,
    selectVehicle,
    selectAssignment,
    selectDriver,
    addRoute,
    updateRoute,
    deleteRoute,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    addAssignment,
    updateAssignment,
    deleteAssignment,
    addDriver,
    updateDriver,
    deleteDriver,
    setLoading,
    setError,
    setEditMode,
    setViewType,
    setViewState,
    updatePaginationParams,
  };
};
